import { apiService } from "@core/api";
import { API_ENDPOINTS } from "@core/constants/endpoints";
import {
  GoalResponse,
  GoalFilters,
  CreateGoalRequestType,
  UpdateGoalRequest,
  GoalApiItem,
  FundingResponse,
  FundingGoal,
  MatchingCallsFilters,
  MatchingCallsResponse,
  MatchingCallsData,
  AnalyzeResponse,
  FundingGoalEntityType,
} from "../model/types";

export class GoalsService {
  async getAllGoals(
    filters?: GoalFilters
  ): Promise<{ count: number; data: FundingGoal[] }> {
    const params = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.append(key, String(value));
        }
      });
    }

    const queryString = params.toString();
    const url = queryString
      ? `${API_ENDPOINTS.GOALS.GET_ALL}?${queryString}`
      : API_ENDPOINTS.GOALS.GET_ALL;

    const response = await apiService.get<FundingResponse>(url);

    return {
      count: response.total_count,
      data: response.data,
    };
  }

  async getSingleGoal(goalId: string): Promise<FundingGoal> {
    const url = API_ENDPOINTS.GOALS.GET_BY_ID(goalId);
    const response = await apiService.get<{ data: FundingGoal }>(url);
    return response.data;
  }

  async analyzeCall(
    goalId: string
  ): Promise<{ status: string; nid: string; goalId: string }> {
    const url = API_ENDPOINTS.GOALS.ANALYZE(goalId);
    const response = await apiService.get<AnalyzeResponse>(url);

    return {
      status: response.status,
      nid: response.nid,
      goalId,
    };
  }

  async getEntity(id: string): Promise<FundingGoalEntityType> {
    // console.log("📡 getEntity called with id:", id);
    // console.log("📡 Using endpoint:", API_ENDPOINTS.GOALS.GET_ENTITY(id));

    const response = await apiService.get<FundingGoalEntityType>(
      API_ENDPOINTS.GOALS.GET_ENTITY(id)
    );

    // console.log("📡 Raw API response:", response);

    // Check if response is wrapped in { data: ... }
    const entityData = (response as any).data || response;

    // console.log("📡 Parsed entity data:", entityData);

    return entityData;
  }

  async getMatchingCalls(
    goalId: string,
    filters?: MatchingCallsFilters
  ): Promise<{
    total_counts: string;
    count: string;
    data: MatchingCallsData[];
  }> {
    const params = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.append(key, String(value));
        }
      });
    }

    const queryString = params.toString();
    const url = queryString
      ? `${API_ENDPOINTS.GOALS.GET_MATCHING_CALLS(goalId)}?${queryString}`
      : API_ENDPOINTS.GOALS.GET_MATCHING_CALLS(goalId);

    const response = await apiService.get<MatchingCallsResponse>(url);

    return {
      total_counts: response.total_count,
      count: response.count,
      data: response.data,
    };
  }

  async deleteGoal(id: string): Promise<void> {
    return apiService.delete<void>(API_ENDPOINTS.GOALS.DELETE(id));
  }

  async createGoal(data: CreateGoalRequestType): Promise<GoalApiItem> {
    return apiService.post<GoalApiItem>(API_ENDPOINTS.GOALS.CREATE, data);
  }

  async updateGoal(id: string, data: UpdateGoalRequest): Promise<GoalApiItem> {
    return apiService.patch<GoalApiItem>(API_ENDPOINTS.GOALS.UPDATE(id), data);
  }
}

export const goalsService = new GoalsService();
